import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { ContentTimestamp } from 'src/components/ContentTimestamp/ContentTimestamp'
import { ContentWriter } from 'src/components/ContentWriter/ContentWriter'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { PremiumContentIcon } from 'src/components/global/PremiumContentIcon/PremiumContentIcon'
import { SignUpModal } from 'src/components/global/SignUpModal'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { InsightCardTags } from 'src/components/Insights/InsightCardTags'
import { IEnum_Content_Contenttype } from 'src/graphql/generated/hooks'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { ContentDataFragment, isContentItem, isContentListItemFragment } from 'src/utils/ContentDataFragment'
import { getContentDefaultImageUrl } from 'src/utils/getContentDefaultImageUrl'
export interface ContentListItemProps {
  item: ContentDataFragment
  showDocumentType?: boolean
  vendor?: boolean
  openTooltipCardId?: string | null
  setOpenTooltipCardId: (id: string | null) => void
  subjectMatterPopup?: number
  contentType?: IEnum_Content_Contenttype
}

export const ContentListItem: React.FC<ContentListItemProps> = ({
  item,
  showDocumentType = false,
  vendor = false,
  openTooltipCardId,
  setOpenTooltipCardId,
  subjectMatterPopup,
  contentType: contentTypeFromProps,
}) => {
  const [showSignUp, setShowSignUp] = useState(false)

  const handleCloseSignUp = () => {
    setShowSignUp(false)
  }

  const subjectMatters = isContentListItemFragment(item)
    ? item.attributes?.subjectMatters
    : isContentItem(item)
      ? item.subjectMatters
      : ''
  const themes = isContentListItemFragment(item) ? item.attributes?.themes : isContentItem(item) ? item.themes : ''
  const vendorTopics = isContentListItemFragment(item)
    ? item.attributes?.vendorTopics
    : isContentItem(item)
      ? item.vendorTopics
      : ''
  let contentTags: any = []
  if (contentTypeFromProps === IEnum_Content_Contenttype.KnowHow) {
    contentTags = [subjectMatters]
      .map((val) => (typeof val === 'string' ? val : ''))
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  } else if (contentTypeFromProps === IEnum_Content_Contenttype.LegaltechResourcesForLawyers) {
    contentTags = [themes]
      .map((val) => (typeof val === 'string' ? val : ''))
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  } else if (contentTypeFromProps === IEnum_Content_Contenttype.VendorResource) {
    contentTags = [vendorTopics]
      .map((val) => (typeof val === 'string' ? val : ''))
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  } else {
    contentTags = [subjectMatters, themes, vendorTopics]
      .map((val) => (typeof val === 'string' ? val : ''))
      .filter((str) => str && str.trim() !== '')
      .join(', ')
  }
  const subjectMatterIds = isContentListItemFragment(item)
    ? item.attributes?.subjectMatterIds
    : isContentItem(item)
      ? item.subjectMatterIds
      : []
  const themeIds = isContentListItemFragment(item)
    ? item.attributes?.themeIds
    : isContentItem(item)
      ? item.themeIds
      : []
  const vendorTopicIds = isContentListItemFragment(item)
    ? item.attributes?.vendorTopicIds
    : isContentItem(item)
      ? item.vendorTopicIds
      : []

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const makeLinks = (linkTo: string, subjectMatterArray: any | undefined, subjectMatterIDs: number[] | undefined) => {
    let goto = 'subject_matter_'
    if (!subjectMatterArray || subjectMatterArray === '' || subjectMatterArray === undefined) {
      return []
    }
    if (linkTo === 'subject_matter') {
      goto = 'know-how/?subject=subject_matter_'
    } else if (linkTo === 'theme') {
      goto = 'legaltech-resources-for-lawyers/?subject=theme_'
    } else if (linkTo === 'vendor_topic') {
      goto = 'resources-for-solution-providers/?vendorTopic='
    }
    if (typeof subjectMatterArray === 'string') {
      return subjectMatterArray?.split(',').map((name: string, index: number) => ({
        name: name.trim(),
        id: subjectMatterIDs?.[index],
        link: `${goto}${subjectMatterIDs?.[index]}`,
      }))
    } else {
      return subjectMatterArray?.data && subjectMatterArray?.data?.length === 0
        ? []
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          subjectMatterArray?.data?.map((el: any, index: number) => ({
            name: el?.attributes?.name?.trim(),
            id: subjectMatterIDs?.[index],
            link: `${goto}${subjectMatterIDs?.[index]}`,
          }))
    }
  }
  const subjectMatterLiks = makeLinks('subject_matter', subjectMatters, subjectMatterIds as number[])
  const themesLiks = makeLinks('theme', themes, themeIds as number[])
  const vendorTopicsLiks = makeLinks('vendor_topic', vendorTopics, vendorTopicIds as number[])

  let contentTagsLink = [subjectMatterLiks, themesLiks, vendorTopicsLiks].flatMap((item) => item)
  if (contentTypeFromProps === IEnum_Content_Contenttype.KnowHow) {
    contentTagsLink = [subjectMatterLiks].flatMap((item) => item)
  } else if (contentTypeFromProps === IEnum_Content_Contenttype.LegaltechResourcesForLawyers) {
    contentTagsLink = [themesLiks].flatMap((item) => item)
  } else if (contentTypeFromProps === IEnum_Content_Contenttype.VendorResource) {
    contentTagsLink = [vendorTopicsLiks].flatMap((item) => item)
  } else {
    contentTagsLink = [subjectMatterLiks, themesLiks, vendorTopicsLiks].flatMap((item) => item)
  }

  const contentType = isContentListItemFragment(item)
    ? item.attributes?.contentType
    : isContentItem(item)
      ? item.contentType
      : IEnum_Content_Contenttype.VendorResource
  const documentType = isContentListItemFragment(item) ? item.attributes?.documentType : item.documentType
  const title = (isContentListItemFragment(item) ? item.attributes?.title : item.title) || ''
  const defaultImageUrl = getContentDefaultImageUrl(item)
  const shouldShowDefaultImage =
    contentType &&
    [
      IEnum_Content_Contenttype.Blog,
      IEnum_Content_Contenttype.IndustryAnalysis,
      IEnum_Content_Contenttype.VendorResource,
      IEnum_Content_Contenttype.KnowHow,
      IEnum_Content_Contenttype.LegaltechResourcesForLawyers,
      IEnum_Content_Contenttype.BuyersGuide,
    ].includes(contentType)
  const snippet =
    (isContentListItemFragment(item) ? item.attributes?.snippet : isContentItem(item) ? item.snippet : item.summary) ||
    ''
  const writer = isContentListItemFragment(item)
    ? item.attributes?.writer
    : isContentItem(item)
      ? item.writer
      : item.author
  const url = isContentListItemFragment(item)
    ? item.attributes?.slug
      ? item.attributes.contentType === IEnum_Content_Contenttype.IndustryAnalysis
        ? `/industry-analysis/${item.attributes?.slug}`
        : `/contents/${item.attributes?.slug}`
      : null
    : isContentItem(item)
      ? `/contents/${item.slug}`
      : item.link
  const isInternalLink = isContentListItemFragment(item) || isContentItem(item)
  const logo = useDefaultLogo(defaultImageUrl)

  const logoContent = (
    <>
      {shouldShowDefaultImage && defaultImageUrl ? (
        <Image
          className="bg-white rounded-[.5rem] drop-shadow-md flex items-center justify-center w-[80px] h-[80px] min-w-[80px] min-h-[80px]"
          src={defaultImageUrl}
          alt={title}
          width={80}
          height={80}
          priority
        />
      ) : contentType ? (
        <PremiumContentIcon contentType={contentType} size="lg" defaultImage={defaultImageUrl} />
      ) : (
        <Image
          className="bg-white rounded-[.5rem] drop-shadow-md flex items-center justify-center w-[80px] h-[80px] min-w-[80px] min-h-[80px]"
          src={logo}
          alt={title}
          width={80}
          height={80}
          priority
        />
      )}
    </>
  )

  useEffect(() => {
    setOpenTooltipCardId(null)
  }, [subjectMatterPopup])

  return (
    <div className="flex flex-row items-center pt-[14px] pb-[14px]">
      <div className="flex flex-row w-full md:items-center">
        <div className="mr-[30px]">
          {url && (
            <Link prefetch={false} className="text-left" href={url} target={isInternalLink ? undefined : '_blank'}>
              {logoContent}
            </Link>
          )}
          {!url && <div className="text-left">{logoContent}</div>}
        </div>

        <div className="grow content-list-cards">
          <div className="w-full flex md:flex-row flex-col justify-between">
            <div className="w-full flex flex-col justify-between">
              <div className="w-full flex justify-between items-center">
                <div className="cursor-pointer text-[13px] text-textdark font-secondary font-medium leading-[20px]">
                  {showDocumentType ? (documentType || '').replace(/_/g, ' ') : (contentType || '').replace(/_/g, ' ')}
                </div>
                <div className="flex">
                  {!vendor && writer && <ContentWriter writer={writer} />}
                  <div className="flex items-center">
                    <div className="mr-3">
                      <ContentTimestamp content={item} />
                    </div>
                  </div>
                  {vendor && (
                    <div className="bg-fog px-[.4rem] py-[.2rem] text-textdark font-bold rounded-[6px] text-[.8rem] tracking-widest leading-5 h-[25px]">
                      Vendor Content
                    </div>
                  )}
                </div>
              </div>
              {url ? (
                <Link
                  prefetch={false}
                  className="text-left text-line-2 cursor-pointer text-[18px] text-textdark font-secondary font-bold mb-1.5"
                  href={url}
                  target={isInternalLink ? undefined : '_blank'}
                >
                  {title}
                </Link>
              ) : (
                <div className="text-left text-line-2 cursor-pointer text-[18px] text-textdark font-secondary font-bold mb-1.5">
                  {title}
                </div>
              )}
            </div>
          </div>
          <HtmlContentViewer
            className="md:pr-[2rem] line-clamp-2 !text-[14px] leading-[18px] !font-medium"
            html={snippet}
          />
          <ThemeOnly theme="legaltech">
            <div className="gap-[8px] mt-[12px] mb-[16px] h-[22px] relative">
              {contentTags && (
                <InsightCardTags
                  cardId={(item?.id || '') as string}
                  tagItems={contentTags}
                  contentTagsLink={contentTagsLink}
                  openTooltipCardId={openTooltipCardId}
                  setOpenTooltipCardId={setOpenTooltipCardId}
                  containerSize={577}
                  subjectMatterPopup={subjectMatterPopup}
                />
              )}
            </div>
          </ThemeOnly>
        </div>
      </div>
      <SignUpModal isOpen={showSignUp} onClose={handleCloseSignUp} />
    </div>
  )
}
